import React, { useEffect, useState } from "react";
import {
    Box, Typography, Paper, Grid, MenuItem, Button, Table, TableBody, 
    TableCell, TableContainer, TableHead, TableRow, IconButton, Stack, 
    TextField, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
    Card, Divider, Autocomplete
} from "@mui/material";
import { 
    CheckCircle, Cancel, Print, Add, History, 
    Person, School, Hotel, ArrowForward, ArrowBack 
} from "@mui/icons-material";
import { collection, getDocs, doc, setDoc, query, where, Timestamp, orderBy } from "firebase/firestore";
import Swal from "sweetalert2";
import { db } from "../../../firebase";

function Passentry() {
    const [passes, setPasses] = useState([]);
    const [open, setOpen] = useState(false);
    const [passType, setPassType] = useState("Student"); // Student, Staff, Hostel
    
    // Form State
    const [formData, setFormData] = useState({
        name: "",
        userId: "",
        reason: "",
        outTime: new Date().toISOString().substring(0, 16), // datetime-local format
        expectedInTime: "",
        destination: "",
        contactNo: ""
    });

    const schoolId = "zIlbJM5BjyrlEhg7fWKs";

    useEffect(() => {
        fetchPasses();
    }, []);

    const fetchPasses = async () => {
        try {
            const q = query(
                collection(db, "gate_passes"), 
                where("schoolId", "==", schoolId),
                orderBy("createdAt", "desc")
            );
            const snap = await getDocs(q);
            setPasses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) { console.error(e); }
    };

    const handleCreatePass = async () => {
        if (!formData.name || !formData.reason) return Swal.fire("Error", "Fill required fields", "error");

        try {
            const passId = "PASS-" + Math.floor(1000 + Math.random() * 9000);
            const newPass = {
                ...formData,
                passNo: passId,
                passCategory: passType,
                schoolId,
                status: "Pending",
                // Multi-level Approval Tracking
                approvals: {
                    warden: passType === "Hostel" ? "Pending" : "N/A",
                    accounts: "Pending",
                    principal: "Pending",
                    parent: passType !== "Staff" ? "Pending" : "N/A"
                },
                createdAt: Timestamp.now(),
                isOut: false,
                isIn: false
            };

            await setDoc(doc(db, "gate_passes", passId), newPass);
            Swal.fire("Success", "Pass Request Sent for Approval", "success");
            setOpen(false);
            fetchPasses();
        } catch (e) { console.error(e); }
    };

    const getStatusColor = (status) => {
        if (status === "Approved") return "success";
        if (status === "Pending") return "warning";
        return "error";
    };

    const handlePrint = (pass) => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <body style="font-family: Arial; padding: 20px; border: 2px solid #000; width: 300px;">
                    <center><h2>GATE PASS</h2></center>
                    <hr/>
                    <p><b>Pass No:</b> ${pass.passNo}</p>
                    <p><b>Name:</b> ${pass.name} (${pass.passCategory})</p>
                    <p><b>Reason:</b> ${pass.reason}</p>
                    <p><b>Out Time:</b> ${pass.outTime}</p>
                    <hr/>
                    <div style="display:flex; justify-content: space-between; margin-top:40px;">
                        <span>Parent Sign</span>
                        <span>Authority Sign</span>
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <Box p={1}>
            <Stack direction="row" justifyContent="space-between" mb={3}>
                <Typography variant="h5" fontWeight="bold">Reception Gate Pass Management</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>Create New Pass</Button>
            </Stack>

            <Grid container spacing={2}>
                {passes.map((pass) => (
                    <Grid item xs={12} md={6} lg={4} key={pass.id}>
                        <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="subtitle2" color="primary" fontWeight="bold">{pass.passNo}</Typography>
                                <Chip label={pass.passCategory} size="small" variant="outlined" />
                            </Stack>
                            
                            <Typography variant="h6" mt={1}>{pass.name}</Typography>
                            <Typography variant="body2" color="text.secondary">{pass.reason}</Typography>
                            
                            <Divider sx={{ my: 1.5 }} />
                            
                            <Typography variant="caption" fontWeight="bold">Approvals Status:</Typography>
                            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                                {Object.entries(pass.approvals).map(([role, status]) => (
                                    status !== "N/A" && (
                                        <Chip 
                                            key={role} 
                                            label={`${role}: ${status}`} 
                                            size="small" 
                                            color={getStatusColor(status)} 
                                            sx={{ fontSize: '10px', mb: 0.5 }} 
                                        />
                                    )
                                ))}
                            </Stack>

                            <Stack direction="row" spacing={1} mt={2}>
                                <Button size="small" variant="outlined" fullWidth startIcon={<Print />} onClick={() => handlePrint(pass)}>Print</Button>
                                <Button size="small" variant="contained" color="error" fullWidth>Cancel</Button>
                            </Stack>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Create Pass Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Generate Outpass Request</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} mt={1}>
                        <Stack direction="row" spacing={1}>
                            <Button variant={passType === "Student" ? "contained" : "outlined"} onClick={() => setPassType("Student")} startIcon={<School />}>Student</Button>
                            <Button variant={passType === "Hostel" ? "contained" : "outlined"} onClick={() => setPassType("Hostel")} startIcon={<Hotel />}>Hostel</Button>
                            <Button variant={passType === "Staff" ? "contained" : "outlined"} onClick={() => setPassType("Staff")} startIcon={<Person />}>Staff</Button>
                        </Stack>

                        <TextField label="Name" fullWidth size="small" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                        <TextField label="User ID / Roll No" fullWidth size="small" value={formData.userId} onChange={e => setFormData({...formData, userId: e.target.value})} />
                        
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField label="Out Date/Time" type="datetime-local" fullWidth size="small" InputLabelProps={{ shrink: true }} value={formData.outTime} onChange={e => setFormData({...formData, outTime: e.target.value})} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Expected In Time" type="datetime-local" fullWidth size="small" InputLabelProps={{ shrink: true }} value={formData.expectedInTime} onChange={e => setFormData({...formData, expectedInTime: e.target.value})} />
                            </Grid>
                        </Grid>

                        <TextField label="Destination / Purpose" multiline rows={2} fullWidth size="small" value={formData.reason} onChange={e => setFormData({...formData, reason: e.target.value})} />
                        <TextField label="Parent/Guardian Contact" fullWidth size="small" value={formData.contactNo} onChange={e => setFormData({...formData, contactNo: e.target.value})} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                    <Button variant="contained" onClick={handleCreatePass}>Request Approval</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Passentry;